import { Formik } from "formik";
import { useEffect, useState } from "react";
import Spin from "../../components/Spin";
import { API, fileUpload, get } from "../../config";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import CustomLabelText from "../../components/CustomLabelText";
import { AddOpeningBalanceSchema } from "../../lib/Validation";
import { Loader2 } from "lucide-react";
import {
  formatSubcontractorName,
  getModuleNamingBySlug,
  MaxLimit,
} from "../../lib/Misc";
import { NumericFormat } from "react-number-format";
import { cn } from "../../lib/utils";
import { useOrganization } from "../../lib/context/OrganisationContext";
import VendorSelect from "../../components/VendorSelect";
import ContactSelect from "../../components/ContactSelect";
import SearchSelect from "../../components/SearchSelect";
import { ContactList, VendorList } from "../../lib/OptionsAPI";
import { DateInputPicker } from "../../components/DateInputPicker";
import moment from "moment";

const AddEditOpeningBalance = ({
  id,
  toggleShowAddEditOpeningBalance,
  edit,
  fetchOpeningBalanceData,
  trustId,
}) => {
  const [openingBalanceData, setOpeningBalanceData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [vendorLoading, setVendorLoading] = useState(false);
  const [vendorListing, setVendorListing] = useState([]);
  const [vendorList, setVendorList] = useState([]);

  const [contactLoading, setContactLoading] = useState(false);
  const [contactListing, setContactListing] = useState([]);
  const [contactList, setContactList] = useState([]);

  const { organizationData } = useOrganization();

  const contactTypeOption = [
    {
      label: `${getModuleNamingBySlug(
        organizationData?.organizationData,
        "vendors",
        "Vendor",
      )}`,
      value: 1,
    },
    {
      label: `${getModuleNamingBySlug(
        organizationData?.organizationData,
        "contacts",
        "Customer",
      )}`,
      value: 2,
    },
  ];

  async function fetchData() {
    try {
      setLoading(true);
      const reponse = await get(`${API.GET_OPENING_BALANCES}/` + id);

      setOpeningBalanceData(reponse?.data?.data);

      let data = reponse?.data?.data;

      if (data?.contactable_id && data?.contactable_type === "vendor") {
        FetchVendorList();
        if (data?.vendor) {
          setVendorList([
            data?.vendor
              ? {
                  ...data?.vendor,

                  value: data?.vendor?.id,
                  label: `${formatSubcontractorName(data?.vendor)}${
                    data?.vendor?.code ? ` (${data?.vendor?.code})` : ""
                  }`,
                }
              : {},
          ]);
        }
      }
      if (data?.contactable_id && data?.contactable_type === "contact") {
        FetchContactList();
        if (data?.vendor) {
          setContactList([
            data?.contact
              ? {
                  ...data?.contact,

                  value: data?.contact?.id,
                  label: `${formatSubcontractorName(data?.contact)}${
                    data?.contact?.code ? ` (${data?.contact?.code})` : ""
                  }`,
                }
              : {},
          ]);
        }
      }
      setLoading(false);
    } catch (e) {
      setOpeningBalanceData(null);
    }
  }
  useEffect(() => {
    if (edit) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const FetchVendorList = async () => {
    await VendorList(
      organizationData,
      trustId,
      1,
      setVendorLoading,
      setVendorList,
      true,
    );
  };

  const FetchContactList = async () => {
    await ContactList(
      organizationData,
      trustId,
      1,
      setContactLoading,
      setContactList,
      true,
    );
  };

  useEffect(() => {
    if (vendorList) {
      let vendorTempListing = vendorList?.map((item) => ({
        ...item,
        value: item?.id,
        label: `${formatSubcontractorName(item)}${
          item?.code ? ` (${item?.code})` : ""
        }`,
      }));
      setVendorListing(vendorTempListing || []);
    } else {
      setVendorListing([]);
    }
  }, [vendorList]);

  useEffect(() => {
    if (contactList) {
      let contactTempListing = contactList?.map((item) => ({
        ...item,
        value: item.id,
        label: `${formatSubcontractorName(item)}${
          item?.code ? ` (${item?.code})` : ""
        }`,
      }));
      setContactListing(contactTempListing || []);
    } else {
      setContactListing([]);
    }
  }, [contactList]);

  const handleFormSubmit = async (value, { setErrors, resetForm }) => {
    const formData = new FormData();
    value.opening_balance = Number(
      `${value?.opening_balance}`.replace(/,/g, ""),
    );

    formData.append("trust_id", trustId);
    formData.append(
      "contactable_id",
      value?.contactable_id ? value?.contactable_id : "",
    );
    formData.append(
      "contactable_type",
      value?.contactable_type ? value?.contactable_type : "",
    );
    formData.append(
      "opening_balance",
      value?.opening_balance ? value?.opening_balance : 0,
    );

    formData.append(
      "transaction_start_date",
      value?.transaction_start_date
        ? moment(value?.transaction_start_date).format("DD-MM-YYYY")
        : "",
    );

    if (edit) {
      formData.append("_method", "put");
    }
    try {
      setLoading(true);
      const { data } = await fileUpload(
        `${API.OPENING_BALANCE_ADD}${edit ? `/${id}` : ""}`,
        formData,
      );
      setLoading(false);
      if (data) {
        toggleShowAddEditOpeningBalance();
        fetchOpeningBalanceData && fetchOpeningBalanceData();
      }
      return data?.data;
    } catch (error) {
      setLoading(false);
    }
  };

  return (
    <Formik
      initialValues={{
        contactable_id: openingBalanceData?.contactable_id,
        contactable_type:
          openingBalanceData?.contactable_type === "vendor"
            ? 1
            : openingBalanceData?.contactable_type === "contact"
              ? 2
              : "",
        opening_balance: openingBalanceData?.opening_balance,
        transaction_start_date: openingBalanceData?.transaction_start_date
          ? moment(openingBalanceData?.transaction_start_date, "DD-MM-YYYY")
          : "",
      }}
      enableReinitialize={true}
      onSubmit={handleFormSubmit}
      validateOnBlur={true}
      validateOnChange={true}
      validationSchema={AddOpeningBalanceSchema(organizationData)}
    >
      {({
        values,
        errors,
        handleChange,
        handleBlur,
        handleSubmit,
        setFieldValue,
        setTouched,
        validateField,
        isValid,
      }) => (
        <>
          {loading && <Spin expand={true} />}
          <form
            onSubmit={handleSubmit}
            noValidate
            className="entry-section md:m-0 m-5"
          >
            <div className="form-box overflow-y-auto overflow-x-hidden p-4 border rounded-lg">
              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="contactable_type">
                    <CustomLabelText
                      required={true}
                      labelText="Contact Type"
                      errorField={errors?.contactable_type}
                    />
                  </Label>
                  <div className="relative">
                    <SearchSelect
                      frameworks={contactTypeOption}
                      optionType={"Contact Type"}
                      handleSelect={(e) => {
                        setFieldValue("contactable_type", e);
                        setFieldValue("contactable_id", "");
                        setTimeout(() => {
                          setTouched({ contactable_type: true });
                          validateField("contactable_type");
                        }, 0);

                        setTimeout(() => {
                          setTouched({ contactable_id: true });
                          validateField("contactable_id");
                        }, 0);
                      }}
                      handleBlur={handleBlur}
                      className={
                        errors?.base_type &&
                        `focus-visible:ring-red-500 border-red-500`
                      }
                      disabled={false}
                      initialValue={values?.contactable_type}
                    />
                  </div>
                </div>

                {values?.contactable_type === 1 ? (
                  <>
                    <div className="grid gap-2">
                      <div className="min-w-0">
                        <Label htmlFor="vendor_id">
                          <CustomLabelText
                            labelText={getModuleNamingBySlug(
                              organizationData?.organizationData,
                              "vendors",
                              "Vendor",
                            )}
                            required={true}
                            errorField={errors?.contactable_id}
                            bottomAlign={true}
                          />
                        </Label>
                        <div className="relative mt-2">
                          <VendorSelect
                            trustId={trustId}
                            frameworkData={vendorListing} // List of vendors
                            optionType={getModuleNamingBySlug(
                              organizationData?.organizationData,
                              "vendors",
                              "Vendor",
                            )}
                            handleSelect={(e) => {
                              setFieldValue("contactable_id", e);
                            }}
                            name="contactable_id"
                            disabled={false}
                            initialValue={
                              values?.contactable_id
                                ? values?.contactable_id
                                : ""
                            }
                            setVendorList={setVendorList} // Update vendor list as new data loads
                            setVendorLoading={setVendorLoading} // Control loading state
                            onClear={() => {
                              setFieldValue("contactable_id", "");
                            }}
                            loading={vendorLoading}
                            className={
                              errors?.contactable_id &&
                              `focus-visible:ring-red-500 border-red-500`
                            }
                          />
                          {vendorLoading && (
                            <span className="absolute top-2 right-2 animate-spin">
                              <Loader2 width={20} height={20} />
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </>
                ) : values?.contactable_type === 2 ? (
                  <>
                    <div className="grid gap-2">
                      <div className="min-w-0">
                        <Label htmlFor="contactable_id">
                          <CustomLabelText
                            labelText={`${getModuleNamingBySlug(
                              organizationData?.organizationData,
                              "contacts",
                              "Customer",
                            )}`}
                            errorField={errors?.contactable_id}
                            required={true}
                          />
                        </Label>
                        <div className="relative mt-2">
                          <ContactSelect
                            trustId={trustId}
                            frameworkData={contactListing} // List of vendors
                            optionType={`${getModuleNamingBySlug(
                              organizationData?.organizationData,
                              "contacts",
                              "Customer",
                            )}`}
                            handleSelect={(e) => {
                              setFieldValue(`contactable_id`, e);
                            }}
                            name="contactable_id"
                            disabled={false}
                            initialValue={
                              values?.contactable_id
                                ? values?.contactable_id
                                : ""
                            }
                            setContactList={setContactList} // Update vendor list as new data loads
                            setContactLoading={setContactLoading} // Control loading state
                            onClear={() => {
                              setFieldValue(`contactable_id`, "");
                            }}
                            className={
                              errors?.contactable_id &&
                              `focus-visible:ring-red-500 border-red-500`
                            }
                            loading={contactLoading}
                          />
                          {contactLoading && (
                            <span className="absolute top-2 right-2 animate-spin">
                              <Loader2 width={20} height={20} />
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <></>
                )}

                <div className="grid gap-1">
                  <CustomLabelText
                    required={false}
                    labelText="Transaction Date"
                    errorField={errors?.transaction_start_date}
                  />
                  <DateInputPicker
                    date={
                      values?.transaction_start_date
                        ? values?.transaction_start_date
                        : ""
                    }
                    onSelect={(e) => {
                      setFieldValue("transaction_start_date", e);
                    }}
                    className={""}
                    inputClassName="w-full"
                    disabledDate={{ after: new Date() }}
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="opening_balance">
                    <CustomLabelText
                      required={true}
                      labelText="Opening Balance($)"
                      errorField={errors?.opening_balance}
                    />
                  </Label>

                  <NumericFormat
                    type="text"
                    name="opening_balance"
                    value={values?.opening_balance}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    decimalScale={2}
                    allowNegative={false}
                    thousandSeparator={true}
                    isAllowed={(values) => MaxLimit(values)}
                    autoComplete="off"
                    onKeyPress={(e) => {
                      e.which === 13 && e.preventDefault();
                    }}
                    className={
                      errors?.opening_balance
                        ? `${cn(
                            "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-webPrimary dark:focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
                          )} focus-visible:ring-red-500 border-red-500`
                        : cn(
                            "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-webPrimary dark:focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
                          )
                    }
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer pt-4 border-t mt-4 flex items-center justify-end">
              <Button type="submit" disabled={!isValid}>
                Save
              </Button>
              <Button
                className="reset ml-3"
                data-dismiss="modal"
                aria-label="Close"
                onClick={(e) => {
                  e.preventDefault();
                  toggleShowAddEditOpeningBalance();
                }}
                variant="secondary"
              >
                Cancel
              </Button>
            </div>
          </form>
        </>
      )}
    </Formik>
  );
};

export default AddEditOpeningBalance;
